import { useState, useCallback, useRef } from "react";

/**
 * Custom hook for batch AI analysis of conversations
 * Handles progress tracking, error handling, and result management
 *
 * @param {Function} analysisFunction - Async function that takes (conversation, messages) and returns analysis result
 * @param {Object} options - Configuration options
 * @returns {Object} - State and functions for batch processing
 */
export const useBatchAIAnalysis = (analysisFunction, options = {}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const abortControllerRef = useRef(null);
  const pausedRef = useRef(false);

  const {
    delayBetweenBatches = 1000,
    onProgress = null,
    onComplete = null,
    onError = null,
  } = options;

  const processBatch = useCallback(
    async (conversations, getMessagesForConversation) => {
      if (isProcessing) {
        console.warn("Batch processing already in progress");
        return;
      }

      setIsProcessing(true);
      setIsPaused(false);
      pausedRef.current = false;
      setProgress({ current: 0, total: conversations.length });
      setResults([]);
      setErrors([]);

      abortControllerRef.current = new AbortController();
      const resultsArray = [];
      const errorsArray = [];

      try {
        for (let i = 0; i < conversations.length; i++) {
          if (abortControllerRef.current?.signal.aborted) break;

          while (pausedRef.current && !abortControllerRef.current?.signal.aborted) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }

          if (abortControllerRef.current?.signal.aborted) break;

          const conversation = conversations[i];

          try {
            const messages = await getMessagesForConversation(conversation._id);
            const analysisResult = await analysisFunction(conversation, messages);

            resultsArray.push({
              conversationId: conversation._id,
              conversation: conversation,
              messages: messages,
              analysis: analysisResult,
              processedAt: new Date().toISOString(),
              success: true,
            });

            const currentProgress = {
              current: i + 1,
              total: conversations.length,
              percentage: Math.round(((i + 1) / conversations.length) * 100),
            };
            setProgress(currentProgress);
            setResults([...resultsArray]);

            if (onProgress) {
              onProgress(currentProgress, resultsArray, conversation);
            }
          } catch (error) {
            console.error(`Error processing conversation ${conversation._id}:`, error);
            errorsArray.push({
              conversationId: conversation._id,
              conversation: conversation,
              error: error.message || "Error desconocido",
              processedAt: new Date().toISOString(),
            });
            setErrors([...errorsArray]);
            if (onError) onError(error, conversation, i);
            const currentProgress = {
              current: i + 1,
              total: conversations.length,
              percentage: Math.round(((i + 1) / conversations.length) * 100),
            };
            setProgress(currentProgress);
          }

          if (i < conversations.length - 1 && delayBetweenBatches > 0) {
            await new Promise((resolve) => setTimeout(resolve, delayBetweenBatches));
          }
        }

        if (onComplete) onComplete(resultsArray, errorsArray);
        return { results: resultsArray, errors: errorsArray };
      } catch (error) {
        console.error("Batch processing error:", error);
        throw error;
      } finally {
        setIsProcessing(false);
      }
    },
    [analysisFunction, isProcessing, delayBetweenBatches, onProgress, onComplete, onError]
  );

  const pause = useCallback(() => {
    setIsPaused(true);
    pausedRef.current = true;
  }, []);

  const resume = useCallback(() => {
    setIsPaused(false);
    pausedRef.current = false;
  }, []);

  const cancel = useCallback(() => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    setIsProcessing(false);
    setIsPaused(false);
    pausedRef.current = false;
  }, []);

  const reset = useCallback(() => {
    setProgress({ current: 0, total: 0 });
    setResults([]);
    setErrors([]);
    setIsProcessing(false);
    setIsPaused(false);
    pausedRef.current = false;
    if (abortControllerRef.current) abortControllerRef.current.abort();
  }, []);

  return {
    processBatch,
    isProcessing,
    progress,
    results,
    errors,
    isPaused,
    pause,
    resume,
    cancel,
    reset,
  };
};
